import React from 'react';
import { Link } from 'react-router-dom';
import { ReservationReturn } from '../clients/rust-api-client';
import { formatMoney } from '../utils/billingHelpers';
import { Fact, Section } from '../violation/linear/panelParts';
import { ReservationStatusIcon } from './ReservationStatusIcon';
import { FeeBreakdown } from './ReservationOperations';
import { depositStatusLabel } from './phaseThreeMeta';
import {
  bookedByLabel,
  cancelOutcomeLabel,
  cancelledByLabel,
  durationMinutes,
  formatDayLabel,
  formatDuration,
  formatLocalTime,
  formatMoment,
  formatNights,
  isZeroMoney,
  localDay,
  reservationFeeStatusLabel,
  statusLabel,
  vehicleSummary,
} from './reservationMeta';

/**
 * The reservation's right-hand Properties panel, laid out like the work
 * order's. Read-only: a booking changes through a decision (approve,
 * deny, cancel), never by editing a field.
 */
export const ReservationPropertiesPanel: React.FC<{ reservation: ReservationReturn }> = ({
  reservation: r,
}) => {
  const minutes = durationMinutes(r.start_local, r.end_local);
  const thisYear = new Date().getFullYear();
  const hasFee = !isZeroMoney(r.fee_amount);
  // A stay (overnight amenity) is described by its nights and its two days.
  const stay = r.nights != null && r.nights > 0;
  const vehicle = vehicleSummary(r);
  const live = r.status === 'pending' || r.status === 'approved';

  return (
    <div className="divide-y divide-line/60">
      <Section title="Properties">
        <Fact label="Status">
          <span className="inline-flex items-center gap-1.5">
            <ReservationStatusIcon status={r.status} />
            {statusLabel(r.status)}
          </span>
        </Fact>
        {stay ? (
          <>
            <Fact label="Check-in">
              {formatDayLabel(localDay(r.start_local), thisYear)}, {formatLocalTime(r.start_local)}
            </Fact>
            <Fact label="Check-out">
              {formatDayLabel(localDay(r.end_local), thisYear)}, {formatLocalTime(r.end_local)}
            </Fact>
            <Fact label="Length">{formatNights(r.nights!)}</Fact>
          </>
        ) : (
          <>
            <Fact label="Day">{formatDayLabel(localDay(r.start_local), thisYear)}</Fact>
            <Fact label="Time">
              {formatLocalTime(r.start_local)} – {formatLocalTime(r.end_local)}
            </Fact>
            {minutes !== null && <Fact label="Length">{formatDuration(minutes)}</Fact>}
          </>
        )}
        <Fact label="Party size">{r.party_size}</Fact>
        {vehicle && <Fact label="Vehicle">{vehicle}</Fact>}
        <p className="px-2 pt-1 text-[12px] text-fg-muted">Times in {r.timezone}</p>
      </Section>

      <Section title="Fee">
        <Fact label="Amount">{hasFee ? formatMoney(r.fee_amount) : 'Free'}</Fact>
        <FeeBreakdown lines={r.fee_breakdown} className="px-2 pb-1" />
        {hasFee && <Fact label="Status">{reservationFeeStatusLabel(r)}</Fact>}
        {r.fee_refundable_until_local && r.status === 'approved' && (
          <Fact label="Free cancel until">
            {formatDayLabel(localDay(r.fee_refundable_until_local), thisYear)},{' '}
            {formatLocalTime(r.fee_refundable_until_local)}
          </Fact>
        )}
        {live && hasFee && r.cancel_outcome_now && (
          <Fact label="If cancelled now">{cancelOutcomeLabel(r.cancel_outcome_now.outcome)}
            {r.cancel_outcome_now.outcome === 'late_fee' && ` (${formatMoney(r.cancel_outcome_now.charge)})`}
          </Fact>
        )}
        {r.deposit_status !== 'none' && (
          <Fact label="Deposit">
            {formatMoney(r.deposit_amount)} · {depositStatusLabel(r.deposit_status)}
          </Fact>
        )}
        {(r.fee_entry_id ||
          r.fee_status === 'refund_due' ||
          r.fee_return_entry_id ||
          r.late_cancel_fee_entry_id ||
          r.deposit_entry_id) && (
          <Fact label="Account">
            <Link
              to={`/hoa/${r.hoa_id}/home/${r.home_id}/billing`}
              className="text-accent-fg hover:underline"
            >
              Open ledger
            </Link>
          </Fact>
        )}
      </Section>

      <Section title="Details">
        <Fact label="Home">
          <Link to={`/hoa/${r.hoa_id}/home/${r.home_id}`} className="text-accent-fg hover:underline">
            {r.home_address}
          </Link>
        </Fact>
        <Fact label="Amenity">
          <Link
            to={`/hoa/${r.hoa_id}/amenities/${r.amenity_id}`}
            className="text-accent-fg hover:underline"
          >
            {r.amenity_name}
          </Link>
        </Fact>
        <Fact label="Booked by">
          {bookedByLabel(r)}
        </Fact>
        <Fact label="Agreement">
          {r.agreement_version
            ? `Version ${r.agreement_version}${r.agreement_accepted_at ? ` · ${formatMoment(r.agreement_accepted_at)}` : ''}`
            : '—'}
        </Fact>
        {r.date_created && <Fact label="Requested">{formatMoment(r.date_created)}</Fact>}
        {r.decided_at && <Fact label="Decided">{formatMoment(r.decided_at)}</Fact>}
        {r.documents_outstanding != null && r.documents_outstanding > 0 && (
          <Fact label="Documents">
            <span className="text-warning">{r.documents_outstanding} still needed</span>
          </Fact>
        )}
        {r.checked_in_at && <Fact label="Checked in">{formatMoment(r.checked_in_at)}</Fact>}
        {r.waitlist_entry_id && <Fact label="From">The waitlist</Fact>}
        {r.pending_expires_at && r.status === 'pending' && (
          <Fact label="Expires">{formatMoment(r.pending_expires_at)}</Fact>
        )}
        {r.cancelled_at && <Fact label="Cancelled">{formatMoment(r.cancelled_at)}</Fact>}
        {cancelledByLabel(r.cancelled_by_kind) && (
          <Fact label={r.status === 'expired' ? 'Ended by' : 'Cancelled by'}>
            {cancelledByLabel(r.cancelled_by_kind)}
          </Fact>
        )}
      </Section>
    </div>
  );
};

export default ReservationPropertiesPanel;
